import React from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import GatherInfoHistoryIntro from './GatherInfoHistoryIntro';
import GatherInfoHistoryHasCurrent from './GatherInfoHistoryHasCurrent';
import GatherInfoHistoryCurrentMethods from './GatherInfoHistoryCurrentMethods';
import GatherInfoHistoryPastMethods from './GatherInfoHistoryPastMethods';
import GatherInfoHistoryPlatforms from './GatherInfoHistoryPlatforms';
import GatherInfoHistoryReview from './GatherInfoHistoryReview';
import { isActivityLowTech } from './ConstActivityBtns';
import { TABS, SUBTABS, HISTORY_STEPS } from './ConstPaths';
import { findOtherIndex, getOtherEntryFromArray, OTHER_CKBX_VAL, OTHER_DISPLAY_SEP } from './OtherCheckboxUtils';

//Manage the wizard form for the Gather Info / History sub-tab
//Initialize the forms with props and manage those as local state.
//On Continue, update the global Session state with the local state from the forms. 
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/


const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  priority: PropTypes.string,
  hasCurrentMethod: PropTypes.string,
  currentMethods: PropTypes.arrayOf(PropTypes.object),
  pastMethods: PropTypes.arrayOf(PropTypes.object),
  platformsUsed: PropTypes.arrayOf(PropTypes.string),
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  priority: '',
  hasCurrentMethod: '',
  currentMethods: [],
  pastMethods: [],
  platformsUsed: []
};

//the steps of the wizard form:
const steps = HISTORY_STEPS;

const BASE_URL = SUBTABS.HISTORY + "/";
const NEXT_SECTION = SUBTABS.GATHER_REVIEW;

class GatherInfoHistory extends React.Component {
  constructor(props) {
    super(props);

    //manage the incoming values as local state
    this.state = {
      hasCurrentMethod: this.props.hasCurrentMethod,
      currentMethods: this.props.currentMethods ?? [], 
      pastMethods: this.props.pastMethods ?? [],
      platformsUsed: this.props.platformsUsed ?? [],
      //local state for 'other' entries
      otherPlatformUsed: getOtherEntryFromArray(this.props.platformsUsed),

    };
    
    this.saveHasCurrentMethod = this.saveHasCurrentMethod.bind(this);
    this.saveCurrentMethods = this.saveCurrentMethods.bind(this);
    this.savePastMethods = this.savePastMethods.bind(this);
    this.savePlatformsUsed = this.savePlatformsUsed.bind(this);

    this.onContinue = this.onContinue.bind(this);
    this.updateCurrentMethods = this.updateCurrentMethods.bind(this);
    this.updatePastMethods = this.updatePastMethods.bind(this);

    console.log("Constructing GatherInfoHistory with currentMethods state: " + this.state.currentMethods);
  }

  //Get the current step from the URL (set up by <Switch> routes in GatherInfo.js)
  getCurrentStep(){
    //e.g. URL of '/gather-info/contexts/intro' would have step of 'intro'
    //props.match: {"step":"intro"}
    //e.g., match: {"path":"/gather-info/history/:step",
    //              "url":"/gather-info/history/current","isExact":true,
    //              "params":{"step":"current"}}
    const stepParam = this.props.match.params.step;
    // console.log("stepParam: " + stepParam);
    if(stepParam){
      // console.log("GatherInfoContexts.getCurrentStep: got step from URL");
      return(stepParam);
    }
    else{
      // console.log("GatherInfoContexts.getCurrentStep: using default step");
      return(steps.INTRO);
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
    const curURL = new URL(window.location.href);


    switch(this.getCurrentStep()){
      case steps.INTRO:
        nextStep = steps.CURRENT;
        break;
      case steps.CURRENT:
        if(this.state.hasCurrentMethod === 'true'){
          if(this.state.currentMethods.length === 0){
            nextStep = steps.CURRENT_METHODS + "/add";
          }
          else nextStep = steps.CURRENT_METHODS;
        }
        else
          nextStep = steps.PAST_METHODS;
        break;
      case steps.CURRENT_METHODS:
        nextStep = steps.PAST_METHODS;
        break;
      case steps.PAST_METHODS:
        //don't ask about platforms if priority activity js low-tech
        if (isActivityLowTech(this.props.priority)){
          nextStep = steps.REVIEW;
        }
        else {
          nextStep = steps.PLATFORMS;
        }
        break;
      case steps.PLATFORMS:
        nextStep = steps.REVIEW;
        break;
      case steps.REVIEW:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.CURRENT;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    in GIH, pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  saveHasCurrentMethod(){
    this.props.updateSession('hasCurrentMethod', this.state.hasCurrentMethod);
  }
  saveCurrentMethods(){
    this.props.updateSession('currentMethods', this.state.currentMethods);
  }
  savePastMethods(){
    this.props.updateSession('pastMethods', this.state.pastMethods);
  }
  savePlatformsUsed(){
    this.props.updateSession('platformsUsed', this.state.platformsUsed);   
  }

  //the keys are the names of the local state variables, defined above in the constructor
  //events come in when users interact with the forms in the child components.
  //for the History section, we use updateLocalState for the 'simpler' data, like
  //hasCurrentMethod and platformsUsed.
  //the currentMethods and pastMethods don't use updateLocalState(); 
  //they're more complicated since they are objects and can involve multiple visits to a form 
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;

      if(key === 'platformsUsed'){
        const newItem=target.value;
        const newItemIndex = this.state.platformsUsed.findIndex((pu) => pu.startsWith(newItem));
        let platformsUsed = [...this.state.platformsUsed];
        //if a platform is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          platformsUsed.push(newItem);
        }
        //platform unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          platformsUsed.splice(newItemIndex, 1);
          if( newItem === OTHER_CKBX_VAL)
            this.setState({ 'otherPlatformUsed': ""});
        }
        value = platformsUsed;
      }

      console.log("Updating GatherInfoHistory local state with key: " + key + " value: " + value);
      if (key === 'otherPlatformUsed')
        this.setState({ [key]: value }, this.adjustPlatformsUsed);
      else this.setState({ [key]: value });
    }
  }

  //after setting 'otherPlatformUsed', adjust the platformsUsed array to include it.
  adjustPlatformsUsed(){
    let otherPlatformUsed = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherPlatformUsed;
    let platformsUsed = [...this.state.platformsUsed];
    const otherItemIndex = findOtherIndex(this.state.platformsUsed);
    if( otherItemIndex > -1){
      platformsUsed[otherItemIndex] = otherPlatformUsed;
      this.setState({ 'platformsUsed': platformsUsed});
    }
  }


  updateCurrentMethods(value){
    this.updateStateDirectly("currentMethods", value);
  }
  updatePastMethods(value){
    this.updateStateDirectly("pastMethods", value);
  }

  //some steps, like currentMethods, have more complicated interaction than just a single form.
  //so for them, the step manages its own forms, then bundles up the needed value (like the array of currentMethods), and sets it directly. 
  updateStateDirectly(key, value){
    console.log("GIH local state update: key: " + key + " with value: " + value);
    this.setState({
      [key]: value
    });
  }

  //If new props come in after rendering, update the views in this tab.
  //(This occurs when you refresh a view and it pulls data from the database.)
  componentDidUpdate(prevProps, prevState){
    //for values that are Arrays:
    if (_.isEqual(this.props.currentMethods, prevProps.currentMethods) === false){
      console.log("--incoming props: " + this.props.currentMethods + " differ from prev props: " + prevProps.currentMethods);
      this.setState({'currentMethods': this.props.currentMethods});
    };
    if (_.isEqual(this.props.pastMethods, prevProps.pastMethods) === false){
      console.log("--incoming props: " + this.props.pastMethods + " differ from prev props: " + prevProps.pastMethods);
      this.setState({'pastMethods': this.props.pastMethods});
    };
    if (_.isEqual(this.props.platformsUsed, prevProps.platformsUsed) === false){
      console.log("--incoming props: " + this.props.platformsUsed + " differ from prev props: " + prevProps.platformsUsed);
      this.setState({'platformsUsed': this.props.platformsUsed});
    };
    //for simple strings
    if (this.props.hasCurrentMethod !== prevProps.hasCurrentMethod){
        console.log("--incoming props: " + this.props.hasCurrentMethod + " differ from prev props: " + prevProps.hasCurrentMethod);
        this.setState({'hasCurrentMethod': this.props.hasCurrentMethod});
    }
  }

  render() {
    console.log("rendering GatherInfoHistory with props: " + JSON.stringify(this.props));
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;
    const priority = this.props.priority;

    return(
    <>
    <Switch>
      <Route path={BASE_URL+ steps.INTRO}>
        <GatherInfoHistoryIntro
          key={steps.INTRO}
          firstName={firstName}
          pronouns={pronouns}
          priority={priority}
          onContinue={this.onContinue}
        />
      </Route>
      <Route path={BASE_URL + steps.CURRENT}>
        <GatherInfoHistoryHasCurrent
          key={steps.CURRENT}
          firstName={firstName}
          pronouns={pronouns}
          priority={priority}
          onContinue={this.onContinue}
          hasCurrentMethod={this.state.hasCurrentMethod}
          onChangeHasCurrentMethod={this.updateLocalState("hasCurrentMethod")}
          saveFormData={this.saveHasCurrentMethod}
        />
      </Route>
      <Route path={BASE_URL + steps.CURRENT_METHODS}>
        <GatherInfoHistoryCurrentMethods
          key={steps.CURRENT_METHODS}
          firstName={firstName}
          pronouns={pronouns}
          onContinue={this.onContinue}
          currentMethods={this.state.currentMethods}
          onChangeCurrentMethods={this.updateCurrentMethods}
          saveFormData={this.saveCurrentMethods}
        />
      </Route>
      <Route path={BASE_URL + steps.PAST_METHODS}>
        <GatherInfoHistoryPastMethods
          key={steps.PAST_METHODS}
          firstName={firstName}
          // pronouns={pronouns}
          onContinue={this.onContinue}
          pastMethods={this.state.pastMethods}
          onChangePastMethods={this.updatePastMethods}
          saveFormData={this.savePastMethods}
        />
      </Route>
      <Route path={BASE_URL + steps.PLATFORMS}>
        <GatherInfoHistoryPlatforms
          key={steps.PLATFORMS}
          firstName={firstName}
          // pronouns={pronouns}
          onContinue={this.onContinue}
          platformsUsed={this.state.platformsUsed}
          onChangePlatformsUsed={this.updateLocalState("platformsUsed")}
          otherPlatformUsed={this.state.otherPlatformUsed}
          onChangeOtherPlatformUsed={this.updateLocalState("otherPlatformUsed")}
          saveFormData={this.savePlatformsUsed}
        />
      </Route>
      <Route path={BASE_URL + steps.REVIEW}>
        <GatherInfoHistoryReview
          key={steps.REVIEW}
          firstName={firstName}
          // pronouns={pronouns}
          priority={priority}
          onContinue={this.onContinue}
          hasCurrentMethod={this.state.hasCurrentMethod}
          currentMethods={this.state.currentMethods}
          pastMethods={this.state.pastMethods}
          platformsUsed={this.state.platformsUsed}
        />
      </Route>
      <Route path={BASE_URL}>
        <GatherInfoHistoryIntro
          key={steps.INTRO}
          firstName={firstName}
          pronouns={pronouns}
          priority={priority}
          onContinue={this.onContinue}
        />
      </Route>
    </Switch>
    </>
    );
  }
}

GatherInfoHistory.propTypes = propTypes;
GatherInfoHistory.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props (used in onContinue)
export default withRouter(GatherInfoHistory);
